'use client';

// App-wide Web Audio engine — the single AudioContext + decoded-buffer registry for
// EVERY sound in the app (UI clicks, RushMid gunfire, music). Web Audio is the right
// tool for mobile and for many overlapping one-shots: each file is decoded ONCE into
// an AudioBuffer; every play spins up a cheap BufferSource, so sounds overlap with no
// latency and without the HTMLAudio limits (few concurrent elements, no overlap, high
// latency on iOS).
//
// Lifted out of components/RushMid/game/sfx.ts so click sounds (and anything else)
// can use the same context app-wide. RushMid's sfx.ts and lib/uiSounds.ts are both
// thin consumers of this engine; the iOS mute-switch bypass below also lives here so
// it covers the ONE context that plays everything.

// Generic UI sounds (shared across every screen — clicks, the roulette tick) live under
// /sounds; only game audio (RushMid gunfire/music/etc.) stays in the /battle asset bundle.
import { getAsset } from './assetStore';

const UI_SOUNDS = new Set(['click', 'buttonclickrelease', 'menu_select', 'achievement_unlock', 'earnings', 'click_wheel', 'item_drop', 'upgrade_fail', 'jackpot', 'no_profit', 'killchicken']);
/** Every generic UI sound name — so the battle preload's prune keep-set retains them too. */
export const UI_SOUND_NAMES: readonly string[] = [...UI_SOUNDS];
// Roulette lives in its own /roulette asset folder (kept out of the /battle bundle).
const ROULETTE_SOUNDS = new Set(['cylinder-spin', 'dry-fire', 'gunshot', 'bullet-load', 'cylinder-close', 'casing-drop']);
// Cache-bust query — bump whenever a sound file's CONTENTS change under the same name (the file
// path is stable, so browsers/CDN would otherwise serve the stale bytes). ?v changes the cache key.
const SOUND_VERSION = 3;
const url = (name: string) =>
  ROULETTE_SOUNDS.has(name)
    ? `/roulette/${name}.mp3?v=${SOUND_VERSION}`
    : `${UI_SOUNDS.has(name) ? '/sounds' : '/battle'}/${name}.mp3?v=${SOUND_VERSION}`;

/** The cache-busted URL a sound is fetched from — exported so a prefetch warms the SAME
 *  cache key the engine later requests (else the warmed copy is a different URL, wasted). */
export const soundUrl = (name: string): string => url(name);

let ctx: AudioContext | null = null;
const buffers: Record<string, AudioBuffer> = {};
// undecoded bytes per sound, kept so we can re-decode into a FRESH context if the old one
// gets closed by the OS on background (a closed context is dead forever — resume() throws).
const raw: Record<string, ArrayBuffer> = {};
const loading = new Set<string>();
let unlockWired = false;

// --- master mute (the "disable all sounds" setting) -------------------------
// Gates EVERY sound (UI clicks, RushMid, case ticks, fanfares) through a master gain
// node, so muting is instant + reversible: the gain drops to 0, loops keep running
// silently and unmute restores them. Persisted so it sticks across sessions. Guarded read
// (some Android WebViews throw on localStorage when partitioned storage is blocked).
const ALL_MUTE_KEY = 'sound:all-muted';
let allMuted = false;
try { allMuted = typeof window !== 'undefined' && window.localStorage.getItem(ALL_MUTE_KEY) === '1'; } catch { /* storage blocked */ }
const allMuteListeners = new Set<() => void>();
let masterGain: GainNode | null = null;

/** Whether ALL sound is muted (the global settings toggle). */
export function isAllMuted(): boolean { return allMuted; }
/** Subscribe to master-mute changes (for useSyncExternalStore in settings). */
export function subscribeAllMuted(cb: () => void): () => void {
  allMuteListeners.add(cb);
  return () => allMuteListeners.delete(cb);
}
/** Mute / unmute every sound app-wide. */
export function setAllMuted(next: boolean): void {
  if (allMuted === next) return;
  allMuted = next;
  try { window.localStorage.setItem(ALL_MUTE_KEY, next ? '1' : '0'); } catch { /* private mode */ }
  if (masterGain) masterGain.gain.value = next ? 0 : 1;
  allMuteListeners.forEach((cb) => cb());
}

// re-decode every already-fetched sound into `c` (a newly created context). decodeAudioData
// detaches its input, so decode a COPY and keep `raw` intact for the next recreation.
function redecodeAll(c: AudioContext): void {
  for (const name of Object.keys(raw)) {
    void c.decodeAudioData(raw[name].slice(0)).then((buf) => { buffers[name] = buf; }).catch(() => undefined);
  }
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  // A backgrounded context can come back CLOSED (mobile Safari / the Telegram WebView).
  // resume() on a closed context throws forever, so sound is gone for good and no gesture
  // brings it back. Drop it + the silent-element binding so a fresh one is built below.
  if (ctx && ctx.state === 'closed') { ctx = null; silentEl = null; silentNode = null; masterGain = null; }
  if (!ctx) {
     
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
     
    if (!AC) return null;
    try { ctx = new AC(); } catch { return null; }
    // master gain sits between every source and the speakers so the global mute is one knob
    masterGain = ctx.createGain();
    masterGain.gain.value = allMuted ? 0 : 1;
    masterGain.connect(ctx.destination);
    redecodeAll(ctx); // rebuild the buffers (they were bound to the dead context)
  }
  return ctx;
}

/** Bring audio back on return-to-foreground: recreate a closed context (via getCtx),
 *  resume, and retry once in case the first resume didn't take (racey suspend/interrupt). */
export function wakeAudio(): void {
  const c = getCtx();
  if (!c) return;
  resumeCtx(c);
  if (c.state !== 'running') {
    window.setTimeout(() => { const cc = getCtx(); if (cc) resumeCtx(cc); }, 250);
  }
  enableMuteBypass();
}

// Resume unless already running. NOT gated on state === 'suspended': after the app is
// backgrounded, iOS/Safari (incl. the Telegram WebView) brings the context back as
// 'interrupted' — a non-standard state that the suspended-only guard skipped, leaving every
// sound dead until the next gesture. Resuming on any non-running state fixes that.
function resumeCtx(c: AudioContext): void {
  if (c.state !== 'running') void c.resume().catch(() => undefined);
}

// iOS/Safari start the AudioContext suspended until a user gesture — resume on the
// first interaction so queued sounds can actually play. Also (re)arms the mute bypass.
export function wireUnlock(): void {
  if (unlockWired || typeof window === 'undefined') return;
  unlockWired = true;
  window.addEventListener('pointerdown', wakeAudio);
  window.addEventListener('touchend', wakeAudio);
  window.addEventListener('keydown', wakeAudio);
}

// --- iOS mute-switch bypass -------------------------------------------------
// A bare AudioContext is routed by iOS to the ringer/ambient channel, which the
// hardware mute switch silences. Continuously playing a SILENT looping <audio>
// element ROUTED THROUGH THIS SAME context (createMediaElementSource → destination)
// forces the context onto the MEDIA channel, which ignores the mute switch — the
// proven web-game trick (feross/unmute-ios-audio, swevans/unmute). Wiring the element
// INTO the WebAudio graph — not leaving it standalone — is the crux: it is what
// actually moves OUR context (not just a detached element) onto the media channel,
// AND keeps a stray now-playing player off the lock screen (the element is owned by
// WebAudio and emits nothing itself). Lifecycle is driven app-wide by
// lib/audioSession.ts (gesture → enable, background → disable).

const repeat = (n: number, s: string): string => { let out = s; for (; n > 1; n--) out += s; return out; };

// ~0.5s of silence as an MP3 data-URI (poor-man's-compressed, expanded at runtime).
const SILENCE =
  'data:audio/mpeg;base64,//uQx' + repeat(23, 'A') +
  'WGluZwAAAA8AAAACAAACcQCA' + repeat(16, 'gICA') + repeat(66, '/') +
  '8AAABhTEFNRTMuMTAwA8MAAAAAAAAAABQgJAUHQQAB9AAAAnGMHkkI' + repeat(320, 'A') +
  '//sQxAADgnABGiAAQBCqgCRMAAgEAH' + repeat(15, '/') +
  '7+n/9FTuQsQH//////2NG0jWUGlio5gLQTOtIoeR2WX////X4s9Atb/JRVCbBUpeRUq' + repeat(18, '/') +
  '9RUi0f2jn/+xDECgPCjAEQAABN4AAANIAAAAQVTEFNRTMuMTAw' + repeat(97, 'V') + 'Q==';

let silentEl: HTMLAudioElement | null = null;
let silentNode: MediaElementAudioSourceNode | null = null;

/** Start (or resume) the silent media-element loop so the mute switch is bypassed.
 *  Idempotent; safe to call on every gesture. No-op while backgrounded. */
export function enableMuteBypass(): void {
  if (typeof document === 'undefined' || document.hidden) return;
  const c = getCtx();
  if (!c) return;
  resumeCtx(c);
  if (!silentEl) {
    silentEl = document.createElement('audio');
    silentEl.loop = true;
    silentEl.preload = 'auto';
    silentEl.disableRemotePlayback = true;
    silentEl.setAttribute('x-webkit-airplay', 'deny');
    silentEl.src = SILENCE;
    // createMediaElementSource is one-shot per element — bind once and keep it.
    try {
      silentNode = c.createMediaElementSource(silentEl);
      silentNode.connect(c.destination);
    } catch { /* unsupported / already bound — the standalone element still helps */ }
  }
  if (silentEl.paused) {
    // pre-gesture rejection is retried on the next gesture; Promise.resolve guards
    // old Safari where play() can return undefined.
    Promise.resolve(silentEl.play()).catch(() => undefined);
  }
}

/** Pause the silent loop (on background) so no stray now-playing UI lingers. */
export function disableMuteBypass(): void {
  if (silentEl && !silentEl.paused) {
    try { silentEl.pause(); } catch { /* ignore */ }
  }
}

// --- buffers ----------------------------------------------------------------

async function loadBuffer(name: string): Promise<void> {
  const c = getCtx();
  if (!c) throw new Error('Web Audio API is unavailable');
  if (buffers[name] || loading.has(name)) return;
  loading.add(name);
  try {
    const blob = await getAsset(url(name)); // IDB-backed: iOS TMA stops re-downloading sounds
    const arr = await blob.arrayBuffer();
    raw[name] = arr.slice(0); // keep undecoded bytes so a recreated context can re-decode
    buffers[name] = await c.decodeAudioData(arr); // detaches `arr` — fine, `raw` has a copy
  } finally {
    loading.delete(name);
  }
}

/** Decode a set of sounds. `onUnit` fires once per newly-decoded sound. */
export async function loadSounds(names: readonly string[], onUnit?: () => void): Promise<void> {
  wireUnlock();
  const c = getCtx();
  if (!c) throw new Error('Web Audio API is unavailable');
  await Promise.all(names.map((n) => loadBuffer(n).then(() => { try { onUnit?.(); } catch { /* ignore */ } })));
}

/** Whether a sound is decoded and ready to play. */
export const isLoaded = (name: string): boolean => !!buffers[name];

/** Fire-and-forget one-shot. Silently no-ops if the buffer isn't decoded yet. */
export function playSfx(name: string, volume = 1): void {
  if (allMuted) return; // global mute — skip spawning a one-shot entirely
  const c = getCtx();
  const buf = buffers[name];
  if (!c || !buf) return;
  resumeCtx(c);
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = volume;
    src.connect(g).connect(masterGain ?? c.destination);
    src.start();
  } catch { /* ignore */ }
}

/** Start a looping buffer; returns a stop() that tears it down. Loads on demand. */
export async function playLoop(name: string, volume = 0.35): Promise<() => void> {
  const c = getCtx();
  if (!c) return () => undefined;
  if (!buffers[name]) {
    try { await loadBuffer(name); } catch { return () => undefined; }
  }
  const buf = buffers[name];
  if (!buf) return () => undefined;
  resumeCtx(c);
  let src: AudioBufferSourceNode | null = null;
  let gain: GainNode | null = null;
  try {
    src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    gain = c.createGain();
    gain.gain.value = volume;
    src.connect(gain).connect(masterGain ?? c.destination);
    src.start();
  } catch { return () => undefined; }
  return () => {
    if (src) { try { src.stop(); } catch { /* ignore */ } try { src.disconnect(); } catch { /* ignore */ } }
    if (gain) { try { gain.disconnect(); } catch { /* ignore */ } }
  };
}
